import { useCallback, useEffect, useMemo, useState } from 'react'
import { Mail, Phone, Search, Trash2, X } from 'lucide-react'
import { adminApi } from '../api'
import { Btn, Card, Modal, Page, TableSkeleton, inputClass } from '../ui'

type Status = 'new' | 'read' | 'replied'

type Row = {
  id: number
  name: string
  email: string
  phone: string
  company: string
  service: string
  status: Status
  ip_address?: string | null
  created_at: string
}

type Counts = { total: number; new: number; read: number; replied: number }

const STATUS_META: Record<Status, { label: string; chip: string; ring: string }> = {
  new: { label: 'New', chip: 'bg-rose-100 text-rose-800', ring: 'border-rose-200' },
  read: { label: 'Read', chip: 'bg-amber-100 text-amber-800', ring: 'border-amber-200' },
  replied: { label: 'Replied', chip: 'bg-emerald-100 text-emerald-800', ring: 'border-emerald-200' },
}

function formatWhen(v: string) {
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function digitsPhone(phone: string) {
  const d = String(phone || '').replace(/\D/g, '')
  if (d.length === 10) return `91${d}`
  return d
}

function bumpCounts(prev: Counts, from: Status, to: Status): Counts {
  if (from === to) return prev
  const next = { ...prev }
  next[from] = Math.max(0, next[from] - 1)
  next[to] = next[to] + 1
  return next
}

export default function SubmissionsPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [counts, setCounts] = useState<Counts>({ total: 0, new: 0, read: 0, replied: 0 })
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | Status>('all')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [selected, setSelected] = useState<Row | null>(null)
  const [selectedSr, setSelectedSr] = useState<number | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 250)
    return () => window.clearTimeout(t)
  }, [q])

  const load = useCallback(async () => {
    setLoading(true)
    const params = new URLSearchParams()
    if (filter !== 'all') params.set('status', filter)
    if (debouncedQ) params.set('q', debouncedQ)
    const qs = params.toString()
    const { data } = await adminApi<{ submissions?: Row[]; counts?: Counts }>(
      `/api/admin/website/submissions${qs ? `?${qs}` : ''}`
    )
    setRows(data.submissions || [])
    if (data.counts) setCounts(data.counts)
    setLoading(false)
  }, [filter, debouncedQ])

  useEffect(() => {
    void load()
  }, [load])

  const filters = useMemo(
    () =>
      [
        { key: 'all' as const, label: 'All', count: counts.total },
        { key: 'new' as const, label: 'New', count: counts.new },
        { key: 'read' as const, label: 'Read', count: counts.read },
        { key: 'replied' as const, label: 'Replied', count: counts.replied },
      ] as const,
    [counts]
  )

  async function setStatus(id: number, status: Status) {
    const current = rows.find((r) => r.id === id) || (selected?.id === id ? selected : null)
    const from = current?.status
    setBusyId(id)
    const { res } = await adminApi(`/api/admin/website/submissions/${id}`, {
      method: 'PATCH',
      json: { status },
    })
    setBusyId(null)
    if (!res.ok) return

    if (filter !== 'all' && filter !== status) {
      setRows((prev) => prev.filter((r) => r.id !== id))
      if (selected?.id === id) setSelected(null)
    } else {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)))
      setSelected((prev) => (prev?.id === id ? { ...prev, status } : prev))
    }
    if (from) setCounts((c) => bumpCounts(c, from, status))
  }

  async function openLead(row: Row, sr: number) {
    setSelected(row)
    setSelectedSr(sr)
    if (row.status === 'new') await setStatus(row.id, 'read')
  }

  async function remove(id: number) {
    if (!confirm('Delete this lead permanently?')) return
    const row = rows.find((r) => r.id === id) || (selected?.id === id ? selected : null)
    setBusyId(id)
    const { res } = await adminApi(`/api/admin/website/submissions/${id}`, { method: 'DELETE' })
    setBusyId(null)
    if (!res.ok) return
    setRows((prev) => prev.filter((r) => r.id !== id))
    if (selected?.id === id) {
      setSelected(null)
      setSelectedSr(null)
    }
    if (row) {
      setCounts((c) => ({
        ...c,
        total: Math.max(0, c.total - 1),
        [row.status]: Math.max(0, c[row.status] - 1),
      }))
    }
  }

  return (
    <Page>
      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-navy-900">Contact leads</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Inbox from the Contact form. Open a lead to view details and update status.
            </p>
          </div>
          {counts.new > 0 && (
            <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-bold text-rose-800">
              {counts.new} new
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {filters.map((f) => {
            const on = filter === f.key
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                  on
                    ? 'border-primary bg-primary text-white shadow-sm'
                    : 'border-primary/15 bg-white text-slate-700 hover:border-primary/35 hover:bg-primary/[0.04]'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 tabular-nums ${on ? 'text-white/80' : 'text-slate-400'}`}>
                  {f.count}
                </span>
              </button>
            )
          })}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className={`${inputClass} pl-9 ${q ? 'pr-9' : ''}`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, company…"
          />
          {q ? (
            <button
              type="button"
              onClick={() => setQ('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {!loading && (
          <p className="text-xs text-slate-500 px-0.5">
            {rows.length === 0
              ? debouncedQ || filter !== 'all'
                ? 'No matches'
                : 'No submissions yet'
              : `Showing ${rows.length} lead${rows.length === 1 ? '' : 's'}`}
          </p>
        )}
      </Card>

      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : rows.length === 0 ? (
        <Card>
          <p className="py-10 text-center text-sm text-slate-500">
            {debouncedQ || filter !== 'all' ? 'No leads match this filter.' : 'No submissions yet.'}
          </p>
        </Card>
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {rows.map((r, idx) => {
              const sr = idx + 1
              const meta = STATUS_META[r.status] || STATUS_META.new
              const isNew = r.status === 'new'
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => void openLead(r, sr)}
                  className={`w-full rounded-2xl border bg-white/80 p-3.5 text-left transition hover:border-primary/30 ${meta.ring} ${
                    isNew ? 'bg-rose-50/40' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-bold text-primary tabular-nums">
                      {sr}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-navy-900 truncate">{r.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{r.company || '—'}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-bold ${meta.chip}`}>
                          {meta.label}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-slate-600 truncate">{r.email}</p>
                      <p className="mt-1 text-[0.7rem] text-slate-400">{formatWhen(r.created_at)}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          <Card className="hidden md:block overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-primary/10 bg-slate-50/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.1em] text-slate-500">
                    <th className="px-4 py-3 w-14">Sr</th>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Lead</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Interest</th>
                    <th className="px-4 py-3 w-28">Status</th>
                    <th className="px-4 py-3 w-36" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const sr = idx + 1
                    const meta = STATUS_META[r.status] || STATUS_META.new
                    const isNew = r.status === 'new'
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-primary/5 last:border-0 hover:bg-primary/[0.03] cursor-pointer transition ${
                          isNew ? 'bg-rose-50/50' : ''
                        }`}
                        onClick={() => void openLead(r, sr)}
                      >
                        <td className="px-4 py-3 tabular-nums text-slate-500 font-medium">{sr}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                          {formatWhen(r.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className={`font-medium text-navy-900 ${isNew ? 'font-semibold' : ''}`}>
                            {r.name}
                          </div>
                          <div className="text-xs text-slate-500">{r.email}</div>
                          <div className="text-xs text-slate-500">{r.phone}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{r.company || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-[220px]">
                          <span className="line-clamp-2">{r.service || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[0.65rem] font-bold ${meta.chip}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <select
                            className="rounded-lg border border-primary/15 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700"
                            value={r.status}
                            disabled={busyId === r.id}
                            onChange={(e) => void setStatus(r.id, e.target.value as Status)}
                          >
                            <option value="new">New</option>
                            <option value="read">Read</option>
                            <option value="replied">Replied</option>
                          </select>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {selected && (
        <Modal
          title={selectedSr != null ? `#${selectedSr} · ${selected.name}` : selected.name}
          onClose={() => {
            setSelected(null)
            setSelectedSr(null)
          }}
          size="md"
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-[0.7rem] font-bold ${
                  STATUS_META[selected.status]?.chip || STATUS_META.new.chip
                }`}
              >
                {STATUS_META[selected.status]?.label || selected.status}
              </span>
              <span className="text-xs text-slate-500">{formatWhen(selected.created_at)}</span>
            </div>

            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</dt>
                <dd className="mt-0.5">
                  <a className="text-primary hover:underline break-all" href={`mailto:${selected.email}`}>
                    {selected.email}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</dt>
                <dd className="mt-0.5">
                  <a className="text-primary hover:underline" href={`tel:${selected.phone.replace(/\s+/g, '')}`}>
                    {selected.phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</dt>
                <dd className="mt-0.5 text-navy-900">{selected.company || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">IP</dt>
                <dd className="mt-0.5 text-slate-600">{selected.ip_address || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Interest / message
                </dt>
                <dd className="mt-1 rounded-xl border border-primary/10 bg-slate-50 px-3 py-2.5 text-slate-700 whitespace-pre-wrap">
                  {selected.service || '—'}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-2">
              <a
                href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: DigiSutra enquiry — ${selected.name}`)}`}
                className="inline-flex items-center gap-1.5 rounded-2xl bg-accent px-3.5 py-2 text-sm font-semibold text-navy-900"
              >
                <Mail className="h-4 w-4" /> Email
              </a>
              <a
                href={`https://wa.me/${digitsPhone(selected.phone)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-2xl border border-primary/15 bg-white px-3.5 py-2 text-sm font-semibold text-primary"
              >
                <Phone className="h-4 w-4" /> WhatsApp
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-primary/10 pt-3">
              <span className="text-xs font-semibold text-slate-500 mr-1">Status</span>
              {(['new', 'read', 'replied'] as Status[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={busyId === selected.id}
                  onClick={() => void setStatus(selected.id, s)}
                  className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                    selected.status === s
                      ? 'border-primary bg-primary text-white'
                      : 'border-primary/15 bg-white text-slate-700 hover:bg-primary/[0.04]'
                  }`}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
              <div className="flex-1" />
              <Btn variant="danger" disabled={busyId === selected.id} onClick={() => void remove(selected.id)}>
                <Trash2 className="h-4 w-4" /> Delete
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </Page>
  )
}
